import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Music, Loader2, Download, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u?.startsWith('/api') ? `${API}${u}` : u);

export const MusicBlock = ({ video, token, onStarted }) => {
  const [prompt, setPrompt] = useState('');
  const [volume, setVolume] = useState(0.3);
  const [busy, setBusy] = useState(false);
  const [featuring, setFeaturing] = useState(false);
  const processing = video.music_status === 'processing';
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const id = video.video_id;

  const addMusic = async () => {
    setBusy(true);
    try {
      const body = { volume };
      if (prompt.trim()) body.prompt = prompt;
      await axios.post(`${API}/api/video/${id}/music`, body, auth);
      toast.success('Composing cinematic music... about 1-2 minutes');
      onStarted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add music');
    }
    setBusy(false);
  };

  const feature = async () => {
    setFeaturing(true);
    try {
      await axios.post(`${API}/api/video/${id}/feature`, {}, auth);
      toast.success('Trailer is now live on the landing page');
      onStarted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to feature video');
    }
    setFeaturing(false);
  };

  return (
    <div className="mt-4 border-t border-slate-700 pt-4" data-testid={`music-block-${id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
          <Music className="w-4 h-4" /> Cinematic Background Music
        </span>
        {video.featured && (
          <span className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-300" data-testid={`featured-badge-${id}`}>On landing page</span>
        )}
      </div>
      {video.music_status === 'completed' && video.scored_url && (
        <div className="mb-3">
          <video controls className="w-full rounded-lg" data-testid={`scored-video-${id}`}>
            <source src={abs(video.scored_url)} type="video/mp4" />
          </video>
          <a href={abs(video.scored_url)} download data-testid={`download-scored-${id}`}
            className="mt-2 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm flex items-center justify-center gap-2 transition-colors">
            <Download className="w-4 h-4" /> Download trailer with music
          </a>
        </div>
      )}
      {video.music_status === 'failed' && (
        <p className="text-sm text-red-400 mb-2" data-testid={`music-error-${id}`}>{video.music_error}</p>
      )}
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={2000}
        placeholder="Optional: describe the music. Leave blank for dark, cinematic Matrix-style score."
        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 h-16 resize-none text-sm"
        data-testid={`music-prompt-${id}`} />
      <label className="flex items-center gap-3 text-xs text-slate-400 mt-2">
        Music volume
        <input type="range" min="0.1" max="0.8" step="0.05" value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))} className="flex-1 accent-emerald-500"
          data-testid={`music-volume-${id}`} />
        <span className="w-10 text-right">{Math.round(volume * 100)}%</span>
      </label>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <button onClick={addMusic} disabled={busy || processing} data-testid={`add-music-btn-${id}`}
          className="py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white rounded-lg text-sm flex items-center justify-center gap-2 transition-colors">
          {busy || processing ? <><Loader2 className="w-4 h-4 animate-spin" /> Composing...</>
            : <><Music className="w-4 h-4" /> {video.scored_url ? 'Re-compose music' : 'Add music'}</>}
        </button>
        <button onClick={feature} disabled={featuring || processing} data-testid={`feature-btn-${id}`}
          className="py-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm flex items-center justify-center gap-2 transition-colors">
          {featuring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} Show on landing page
        </button>
      </div>
    </div>
  );
};
